
import Image, { ImageProps } from 'next/image';
import { useEffect, useState } from 'react';

/**
 * DeferredImage - A client-side wrapper around Next.js Image component for SSR hydration optimization
 * 
 * ⚠️ WARNING: You should use `next/image` directly unless you are certain you need this component.
 * 
 * This component intentionally delays image loading until after all JavaScript has been processed 
 * and JSX has rendered on the client side. It prevents images from being loaded during SSR hydration,
 * ensuring that the initial render completes faster by deferring image loading until after the 
 * component mounts.
 * 
 * **How it works:**
 * - During SSR and initial hydration, images are not prioritized
 * - After the component mounts (useEffect), the priority prop is set to trigger image loading
 * - This ensures JavaScript execution and JSX rendering complete before images start loading
 * 
 * **When to use this:**
 * - When you need to optimize SSR hydration performance by deferring image loading
 * - When images are causing performance bottlenecks during initial page render
 * - When you want to ensure interactive elements load before images
 * 
 * **When NOT to use this:**
 * - For standard image usage (use `next/image` directly)
 * - For above-the-fold images that should load immediately (use `next/image` with priority)
 * - For critical images that are part of the Largest Contentful Paint (LCP)
 * 
 * @example
 * ```tsx
 * // Prefer this for most cases:
 * import Image from 'next/image';
 * <Image src="/photo.jpg" alt="Photo" width={500} height={300} />
 * 
 * // Use DeferredImage only when intentionally deferring image loading:
 * import NextImage from '@/components/image/DeferredImage';
 * <DeferredImage src="/photo.jpg" alt="Photo" width={500} height={300} />
 * ```
 */
const DeferredImage = ({ src, alt, ...props }: Omit<ImageProps, 'priority' | 'loading'>) => {
  const [loadImage, setLoadImage] = useState(false);
  
  useEffect(() => {
    setLoadImage(true);
  }, []);

  return <Image src={src} alt={alt} {...props} priority={loadImage} />;
};

export default DeferredImage;